import { useState } from "react";
import { useOne } from "@refinedev/core";
import {
  Card,
  Table,
  Button,
  Empty,
  Tag,
  Space,
  Row,
  Col,
  Typography,
  Spin,
  Breadcrumb,
  Tooltip,
  Popconfirm,
  message,
  Modal,
  App,
} from "antd";
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  EditOutlined,
  FileTextOutlined,
  HomeOutlined,
  UserOutlined,
  CheckCircleOutlined,
  StopOutlined,
  GlobalOutlined,
  TagsOutlined,
  AreaChartOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  SwapOutlined,
  PushpinOutlined,
} from "@ant-design/icons";
import apiClient from "../../services/api/apiClient";
import { Statistic } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useParams } from "react-router";
import dayjs from "dayjs";

import { RealPlotDetailModal } from "./components/RealPlotDetailModal";
import { MovePlotRealModal } from "./components/MovePlotRealModal";
import { SurfaceValidationCard } from "../../components/SurfaceValidationCard";
import { useGetIdentity } from "@refinedev/core";
import { ADMIN_USER_TYPES, canWriteData } from "../../types/auth";
import type { UserInfo } from "../../types/auth";
import { FarmMapCard } from "./components/FarmMapCard";

/* ------------------------------------------------------------------ */
/* Tipos                                                              */
/* ------------------------------------------------------------------ */

interface VarietyBreakdown {
  id: number | null;
  code: string;
  name: string;
  licensed_surface: number;
  audited_surface: number;
  surface?: number; // legacy alias
}

interface VarietyChip {
  id: number;
  code: string;
  name: string;
}

interface AuditItem {
  id: number;
  reference: string;
  start_date: string | null;
  auditor_name: string | null;
  audit_type_name: string | null;
  audit_state_id: number | null;
  audit_state_name: string | null;
  season_year: number | null;
  total_kg: number | null;
  stimated_production: number | null;
  audit_act_file: string | null;
  audit_plots_count?: number;
  varieties?: VarietyChip[];
}

// Color por estado canónico — sigue la paleta del flujo:
// planificada → en curso → finalizada (azul → naranja → verde).
// Estados "no realizada" y "cancelada" en gris/rojo.
const auditStateColor = (name: string | null): string => {
  const n = (name || "").toLowerCase();
  if (n.includes("planificad")) return "blue";
  if (n.includes("sin informe")) return "orange";
  if (n.includes("con informe")) return "gold";
  if (n.includes("checke")) return "purple";
  if (n.includes("finaliz")) return "green";
  if (n.includes("no realiz")) return "default";
  if (n.includes("cancel")) return "red";
  return "default";
};

interface ReferencePlot {
  id: number;
  sigpac_ref: string | null;
  surface: number;
  surface_gps: number | null;
  poligono: number | null;
  parcela: number | null;
  recinto: number | null;
  estimated_planting_date: string | null;
  has_geom: boolean;
  geom_geojson: GeoJSON.Geometry | null;
  variety_id: number | null;
  variety_code: string | null;
  variety_name: string | null;
  is_audit: boolean;
  is_illegal: boolean;
}

interface RealPlot {
  id: number;
  name: string;
  surface: number | null;
  surface_gps: number | null;
  plants: number | null;
  crop_date: number | null;
  plantation_date: string | null;
  variety: string | null;
  variety_id: number | null;
  variety_code: string | null;
  plot_type_name: string | null;
  rootstock_name: string | null;
  plantation_frame: string | null;
  season_name: string | null;
  is_active: boolean;
  status: "open" | "closed";
  closed_date: string | null;
  comment_closed: string | null;
  created_in_audit: { id: number; start_date: string | null; reference: string | null } | null;
  closed_in_audit: { id: number; start_date: string | null; reference: string | null } | null;
  has_geom: boolean;
  geom_geojson: GeoJSON.Geometry | null;
  // 'canonical' cuando `PlotReal.geom` está poblada;
  // 'historical' cuando el mapa está pintando el fallback tomado de
  // una AuditPlot histórica (parcela canónica sin polígono propio,
  // pero auditada al menos una vez con geometría);
  // null cuando no hay polígono en ningún sitio.
  geom_source: "canonical" | "historical" | null;
}

interface ProductionRecord {
  id: number;
  season_year: number | null;
  variety_id: number | null;
  variety_code: string | null;
  variety_name: string | null;
  audited_kg: number | null;
  produced_kg: number | null;
  packers: string;
}

interface FarmDetail {
  id: number;
  name: string;
  producer: number;
  producer_id: number;
  producer_name: string;
  country_name: string;
  province_name: string;
  town_name: string;
  variety_display: string;
  production_type: string;
  is_under_net: boolean | string;
  is_active: boolean;
  surface_tango: number;
  total_surface: number;
  audited_surface: number;
  // PR4 — nuevos campos del detalle.
  varieties: VarietyBreakdown[];
  licensed_surface_total: number;
  audited_surface_total: number;
  surface_diff_total: number;
  audits: AuditItem[];
  reference_plots: ReferencePlot[];
  real_plots: RealPlot[];
  production_records: ProductionRecord[];
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const fmtNum = (val: number | null | undefined, decimals = 2) =>
  val != null
    ? Number(val).toLocaleString("es-ES", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "—";

const fmtKg = (val: number | null | undefined) =>
  val != null
    ? Number(val).toLocaleString("es-ES", { maximumFractionDigits: 0 })
    : "—";

const fmtDate = (val: string | null | undefined) =>
  val ? dayjs(val).format("DD/MM/YYYY") : "—";

const renderDiff = (
  licensed: number,
  audited: number,
  withPct = true,
) => {
  const diff = audited - licensed;
  if (licensed === 0 && audited === 0) return "—";
  const absDiff = Math.round(Math.abs(diff) * 100) / 100;
  const pct = licensed > 0 ? (Math.abs(diff) / licensed) * 100 : 0;
  const color =
    absDiff === 0 ? "#1677ff" : diff > 0 ? "#f5222d" : "#52c41a";
  const sign = absDiff === 0 ? "" : diff > 0 ? "+" : "";
  return (
    <span style={{ color, fontWeight: 500 }}>
      {sign}
      {diff.toFixed(2)} ha
      {withPct && licensed > 0 && ` (${pct.toFixed(1)}%)`}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Componente                                                         */
/* ------------------------------------------------------------------ */

export const FarmShow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  // Ant Design 5: `Modal.confirm()` estático NO se abre cuando la
  // app está wrappeada en `<App>` (nuestro caso — el ConfigProvider
  // + App envuelve todo el árbol para theme/message/notification
  // consistente). Hay que usar `modal.confirm` del hook `App.useApp()`
  // que sí participa en el context. Aplica también a message/notification.
  const { modal } = App.useApp();

  // Estado del modal "Detalle de parcela canónica". `null` ⇒ cerrado.
  const [selectedPlotRealId, setSelectedPlotRealId] = useState<number | null>(null);
  // Cual PlotReal está pendiente de borrarse — controla el loading
  // del icono y bloquea clicks repetidos.
  const [deletingPlotRealId, setDeletingPlotRealId] = useState<number | null>(
    null,
  );
  // Fase 4 refactor doblados: selección para fusión de PlotReals.
  // Solo se activa cuando el admin marca >=2 filas; abajo aparece
  // el botón "Fusionar" que consume `POST /api/plot-reals/merge/`.
  const [mergeSelectedIds, setMergeSelectedIds] = useState<number[]>([]);
  const [merging, setMerging] = useState(false);
  // Modal "Trasladar parcela canónica a otra finca". Se rellena con
  // el objeto RealPlot completo para pasar id+nombre al modal.
  const [moveTarget, setMoveTarget] = useState<{
    id: number;
    name: string | null;
  } | null>(null);
  // Loading del botón "Adoptar geometría histórica" por parcela — evita
  // clicks repetidos mientras el POST está en vuelo.
  const [adoptingGeomId, setAdoptingGeomId] = useState<number | null>(null);

  const { query } = useOne<FarmDetail>({
    resource: "farms",
    id: id!,
    queryOptions: { enabled: !!id },
  });
  const record = query.data?.data;
  const isLoading = query.isLoading;
  const { data: identity } = useGetIdentity<UserInfo>();
  const isAdmin = (ADMIN_USER_TYPES as readonly number[]).includes(
    identity?.app_user?.user_type ?? -1,
  );
  /**
   * `canWrite` = puede editar/crear/borrar. Los ESA Admin (Eurosemillas)
   * scoped son solo-lectura por política — pueden ver y descargar
   * (bundles, mapas, PDFs) pero no tocar datos. Los botones de edición,
   * borrado y creación se ocultan cuando `!canWrite`.
   */
  const canWrite = canWriteData(identity);

  const fromState = (location.state as any)?.from;
  // Snapshot opcional del listado (URL + scrollY) capturado al venir
  // desde /farms — lo usa el botón Volver para restaurar filtros y
  // posición de scroll del listado.
  const listSnapshot = (location.state as any)?.listSnapshot as
    | { search: string; scrollY: number }
    | undefined;
  const backUrl =
    fromState === "producer" && record?.producer_id
      ? `/producers/show/${record.producer_id}`
      : "/farms";

  const handleBack = () => {
    if (fromState === "farms" && listSnapshot) {
      navigate(`/farms${listSnapshot.search || ""}`, {
        state: { restoreScroll: listSnapshot.scrollY },
      });
      return;
    }
    navigate(backUrl);
  };

  // Borrado destructivo de PlotReal. Las AuditPlot referenciadas se
  // quedan (SET_NULL), pero los snapshots/revisiones de la propia
  // parcela canónica se eliminan en cascada. Solo admin.
  // Copia a `PlotReal.geom` la geometría del último AuditPlot histórico
  // que la tenía. Sólo el backend decide cuál es "el último" — aquí
  // lanzamos el POST y refrescamos.
  const handleAdoptHistoricalGeom = async (plotRealId: number) => {
    setAdoptingGeomId(plotRealId);
    try {
      await apiClient.post(
        `/plot-reals/${plotRealId}/adopt-historical-geom/`,
      );
      message.success(
        t(
          "farms.show.adopt_geom_success",
          "Geometría histórica adoptada como canónica",
        ),
      );
      query.refetch();
    } catch (error: any) {
      message.error(
        error?.response?.data?.detail ||
          error?.message ||
          t(
            "farms.show.adopt_geom_error",
            "No se pudo adoptar la geometría histórica",
          ),
      );
    } finally {
      setAdoptingGeomId(null);
    }
  };

  const handleDeletePlotReal = async (plotRealId: number) => {
    setDeletingPlotRealId(plotRealId);
    try {
      await apiClient.delete(`/plot-reals/${plotRealId}/`);
      message.success(
        t(
          "farms.show.delete_plot_real_success",
          "Parcela canónica eliminada",
        ),
      );
      // Refetch para que la tabla y el mapa reflejen el cambio.
      query.refetch();
    } catch (error: any) {
      message.error(
        error?.response?.data?.detail ||
          error?.message ||
          t(
            "farms.show.delete_plot_real_error",
            "No se pudo eliminar la parcela canónica",
          ),
      );
    } finally {
      setDeletingPlotRealId(null);
    }
  };

  const breadcrumbItems = [
    {
      title: <HomeOutlined />,
      href: "/",
      onClick: (e: any) => {
        e.preventDefault();
        navigate("/");
      },
    },
    ...(fromState === "producer" && record?.producer_id
      ? [
          {
            title: (
              <>
                <UserOutlined /> {t("producers.title", "Productores")}
              </>
            ),
            href: "/producers",
            onClick: (e: any) => {
              e.preventDefault();
              navigate("/producers");
            },
          },
          {
            title: record.producer_name || `Productor #${record.producer_id}`,
            href: `/producers/show/${record.producer_id}`,
            onClick: (e: any) => {
              e.preventDefault();
              navigate(`/producers/show/${record.producer_id}`);
            },
          },
        ]
      : [
          {
            title: (
              <>
                <EnvironmentOutlined /> {t("farms.title", "Fincas")}
              </>
            ),
            href: "/farms",
            onClick: (e: any) => {
              e.preventDefault();
              navigate("/farms");
            },
          },
        ]),
    { title: record?.name || `Finca #${id}` },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!record) {
    return <Empty description={t("farms.show.not_found", "Finca no encontrada")} />;
  }

  /* ============================================================== */
  /* Sección 1 — Cabecera + info general                            */
  /* ============================================================== */

  const topBar = (
    <>
      <Breadcrumb style={{ marginBottom: 12 }} items={breadcrumbItems} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Typography.Title level={4} style={{ margin: 0 }}>
          {record.name}{" "}
          <Tag
            color={record.is_active ? "green" : "red"}
            style={{ marginLeft: 8, fontSize: 13 }}
          >
            {record.is_active ? "Activa" : "Inactiva"}
          </Tag>
        </Typography.Title>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t("common.back", "Volver")}
          </Button>
          {canWrite && (
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                navigate(
                  `/producers/${record.producer_id}/farms/edit/${record.id}`,
                  {
                    state: {
                      from:
                        fromState === "producer" ? "producer" : "farm-show",
                      farmId: record.id,
                    },
                  },
                )
              }
            >
              {t("common.edit", "Editar")}
            </Button>
          )}
          {record.reference_plots.some((p) => p.has_geom) && (
            <Button
              type="primary"
              icon={<EnvironmentOutlined />}
              onClick={() =>
                navigate(`/farms/map/${record.id}`, { state: { from: fromState } })
              }
            >
              {t("farms.show.view_map", "Ver en mapa")}
            </Button>
          )}
        </Space>
      </div>
    </>
  );

  const fieldRow = (
    icon: React.ReactNode,
    label: string,
    value: React.ReactNode,
  ) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 0",
        borderBottom: "1px dashed #f0f0f0",
      }}
    >
      <Space size={6} style={{ color: "#8c8c8c", fontSize: 12 }}>
        {icon}
        {label}
      </Space>
      <span style={{ fontSize: 13, textAlign: "right" }}>{value}</span>
    </div>
  );

  const licensed = record.licensed_surface_total ?? 0;
  const audited = record.audited_surface_total ?? 0;
  const surfaceDiff = audited - licensed;

  const headerInfoSection = (
    <Card
      size="small"
      title={
        <Space>
          <HomeOutlined />
          <strong>Información de la finca</strong>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: 12 }}
    >
      {/* KPIs destacados */}
      <Row gutter={8} style={{ marginBottom: 12 }}>
        <Col xs={12} sm={6}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
            <Statistic
              title="Variedades"
              value={record.varieties?.length ?? 0}
              prefix={<TagsOutlined style={{ fontSize: 14 }} />}
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
            <Statistic
              title="Sup. licenciada"
              value={licensed}
              precision={2}
              suffix="ha"
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
            <Statistic
              title="Sup. auditada"
              value={audited}
              precision={2}
              suffix="ha"
              valueStyle={{ fontSize: 18 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" bodyStyle={{ padding: "10px 12px" }}>
            <Statistic
              title="Diferencia"
              value={surfaceDiff}
              precision={2}
              suffix="ha"
              valueStyle={{
                fontSize: 18,
                color:
                  Math.abs(surfaceDiff) < 0.01
                    ? undefined
                    : surfaceDiff > 0
                      ? "#52c41a"
                      : "#cf1322",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Datos descriptivos */}
      <Row gutter={12}>
        <Col xs={24} md={12}>
          {fieldRow(
            <UserOutlined />,
            "Productor",
            <a
              onClick={() =>
                navigate(`/producers/show/${record.producer_id}`, {
                  state: { from: "farm", farmId: record.id },
                })
              }
              style={{ fontWeight: 500 }}
            >
              {record.producer_name || "—"}
            </a>,
          )}
          {fieldRow(
            <EnvironmentOutlined />,
            "Municipio (Provincia)",
            record.town_name && record.province_name
              ? `${record.town_name} (${record.province_name})`
              : record.town_name || record.province_name || "—",
          )}
          {fieldRow(
            <GlobalOutlined />,
            "País",
            record.country_name || "—",
          )}
        </Col>
        <Col xs={24} md={12}>
          {fieldRow(
            <AreaChartOutlined />,
            "Tipo Producción",
            record.production_type ? (
              <Tag color="geekblue">{record.production_type}</Tag>
            ) : (
              "—"
            ),
          )}
          {fieldRow(
            <AppstoreOutlined />,
            "Bajo Malla",
            record.is_under_net === true ||
              record.is_under_net === "si" ||
              record.is_under_net === "yes" ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Sí
              </Tag>
            ) : (
              <Tag icon={<StopOutlined />}>No</Tag>
            ),
          )}
          {fieldRow(
            <HomeOutlined />,
            "Estado",
            record.is_active ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                Activa
              </Tag>
            ) : (
              <Tag color="red" icon={<StopOutlined />}>
                Inactiva
              </Tag>
            ),
          )}
        </Col>
      </Row>
    </Card>
  );

  /* ============================================================== */
  /* Sección 2 — Superficies por variedad                            */
  /* ============================================================== */

  const varietiesSection = (
    <Card
      size="small"
      title={
        <Space>
          <strong>
            {t("farms.show.variety_section", "Superficies por variedad protegida")}
          </strong>
          <Tag color="blue">
            {record.varieties?.length ?? 0}{" "}
            {(record.varieties?.length ?? 0) === 1
              ? "variedad"
              : "variedades"}
          </Tag>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 16 }}
    >
      {record.varieties && record.varieties.length > 0 ? (
        <Table
          dataSource={record.varieties}
          rowKey="code"
          size="small"
          pagination={false}
          summary={() => {
            const licensed = record.licensed_surface_total ?? 0;
            const audited = record.audited_surface_total ?? 0;
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{licensed.toFixed(2)} ha</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <strong>{audited.toFixed(2)} ha</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    {renderDiff(licensed, audited)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        >
          <Table.Column
            title="Variedad"
            dataIndex="name"
            render={(v: string) => <Tag color="blue">{v}</Tag>}
          />
          <Table.Column
            title="Sup. Licenciada"
            dataIndex="licensed_surface"
            align="right"
            width={160}
            render={(v: number) =>
              `${(Number(v) || 0).toFixed(2)} ha`
            }
          />
          <Table.Column
            title="Sup. Auditada"
            dataIndex="audited_surface"
            align="right"
            width={160}
            render={(v: number) =>
              `${(Number(v) || 0).toFixed(2)} ha`
            }
          />
          <Table.Column
            title="Diferencia"
            align="right"
            width={180}
            render={(_: any, row: VarietyBreakdown) =>
              renderDiff(
                Number(row.licensed_surface) || 0,
                Number(row.audited_surface) || 0,
              )
            }
          />
        </Table>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin variedades asignadas"
        />
      )}
    </Card>
  );

  /* ============================================================== */
  /* Sección 3 — Parcelas declaradas (Plots)                         */
  /* ============================================================== */

  const referencePlotsSection = (
    <Card
      size="small"
      title={
        <Space>
          <strong>
            {t(
              "farms.show.reference_plots_section",
              "Parcelas declaradas (contrato)",
            )}
          </strong>
          <Tag>{record.reference_plots.length}</Tag>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 16 }}
    >
      {record.reference_plots.length > 0 ? (
        <Table
          dataSource={record.reference_plots}
          rowKey="id"
          size="small"
          pagination={
            record.reference_plots.length > 10
              ? { pageSize: 10, showSizeChanger: true }
              : false
          }
          scroll={{ x: 900 }}
        >
          <Table.Column
            title="SIGPAC (P-P-R)"
            dataIndex="sigpac_ref"
            width={140}
            render={(v: string | null) => v || "—"}
            sorter={(a: ReferencePlot, b: ReferencePlot) =>
              (a.sigpac_ref || "").localeCompare(b.sigpac_ref || "")
            }
          />
          <Table.Column
            title="Variedad protegida"
            dataIndex="variety_name"
            width={180}
            render={(v: string | null) =>
              v ? <Tag color="blue">{v}</Tag> : "—"
            }
            sorter={(a: ReferencePlot, b: ReferencePlot) =>
              (a.variety_name || "").localeCompare(b.variety_name || "")
            }
          />
          <Table.Column
            title="Sup. Licenciada (ha)"
            dataIndex="surface"
            width={150}
            align="right"
            render={(v: number) => fmtNum(v)}
            sorter={(a: ReferencePlot, b: ReferencePlot) =>
              (a.surface ?? 0) - (b.surface ?? 0)
            }
          />
          <Table.Column
            title="Sup. GPS (ha)"
            dataIndex="surface_gps"
            width={130}
            align="right"
            render={(v: number | null) =>
              v != null ? (
                <Tooltip title="Calculada a partir del polígono">
                  {fmtNum(v)}
                </Tooltip>
              ) : (
                "—"
              )
            }
            sorter={(a: ReferencePlot, b: ReferencePlot) =>
              (a.surface_gps ?? 0) - (b.surface_gps ?? 0)
            }
          />
          <Table.Column
            title="Fecha estim. plantación"
            dataIndex="estimated_planting_date"
            width={170}
            render={(v: string | null) => (v ? fmtDate(v) : "—")}
          />
          <Table.Column
            title="Estado"
            width={140}
            render={(_: any, p: ReferencePlot) => (
              <Space size={4} wrap>
                {p.is_audit && (
                  <Tooltip title="Esta parcela ya ha sido auditada en algún momento">
                    <Tag color="green">Auditada</Tag>
                  </Tooltip>
                )}
                {p.is_illegal && <Tag color="red">Ilegal</Tag>}
                {p.has_geom && (
                  <Tooltip title="Tiene geometría dibujada">
                    <Tag icon={<EnvironmentOutlined />}>Geo</Tag>
                  </Tooltip>
                )}
              </Space>
            )}
          />
        </Table>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin parcelas declaradas en contrato"
        />
      )}
    </Card>
  );

  /* ============================================================== */
  /* Sección 4 — Parcelas canónicas (PlotReals)                      */
  /* ============================================================== */

  const handleMergePlotReals = () => {
    if (mergeSelectedIds.length < 2) return;
    const targetId = mergeSelectedIds[0];
    const absorbedIds = mergeSelectedIds.slice(1);
    const nameOf = (id: number) =>
      record.real_plots.find((p) => p.id === id)?.name || `#${id}`;
    modal.confirm({
      title: "Fusionar parcelas canónicas",
      content: (
        <div>
          <p>
            <strong>{nameOf(targetId)}</strong> conservará su identidad
            (id, geometría, superficie, snapshots, revisiones).
          </p>
          <p>
            Las siguientes se absorberán y sus <em>plantings</em> pasarán
            a la que se conserva; después se eliminarán:
          </p>
          <ul>
            {absorbedIds.map((id) => (
              <li key={id}>{nameOf(id)}</li>
            ))}
          </ul>
          <p>Esta operación no se puede deshacer.</p>
        </div>
      ),
      okText: "Fusionar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          setMerging(true);
          await apiClient.post("/plot-reals/merge/", {
            target_id: targetId,
            absorbed_ids: absorbedIds,
          });
          setMergeSelectedIds([]);
          await query.refetch();
        } catch (err: any) {
          modal.error({
            title: "No se pudo fusionar",
            content:
              err?.response?.data?.detail ??
              String(err?.message ?? "Error desconocido"),
          });
        } finally {
          setMerging(false);
        }
      },
    });
  };

  const realPlotsSection = (
    <Card
      size="small"
      title={
        <Space>
          <strong>
            {t(
              "farms.show.real_plots_section",
              "Parcelas canónicas (tras auditorías)",
            )}
          </strong>
          <Tag>
            {record.real_plots.filter((p) => p.is_active).length} activas
          </Tag>
          {record.real_plots.some((p) => !p.is_active) && (
            <Tag color="default">
              {record.real_plots.filter((p) => !p.is_active).length} cerradas/inactivas
            </Tag>
          )}
        </Space>
      }
      extra={
        canWrite && mergeSelectedIds.length >= 2 ? (
          <Space>
            <Button
              danger
              onClick={handleMergePlotReals}
              loading={merging}
              icon={<CheckCircleOutlined />}
            >
              Fusionar {mergeSelectedIds.length} parcelas
            </Button>
            <Button
              onClick={() => setMergeSelectedIds([])}
              disabled={merging}
            >
              Cancelar
            </Button>
          </Space>
        ) : canWrite ? (
          <Tag color="blue" style={{ fontSize: 12 }}>
            Marca 2+ filas para fusionar
          </Tag>
        ) : null
      }
      bordered={false}
      style={{ marginBottom: 16 }}
    >
      {record.real_plots.length > 0 ? (
        <Table
          dataSource={record.real_plots}
          rowKey="id"
          size="small"
          pagination={
            record.real_plots.length > 10
              ? { pageSize: 10, showSizeChanger: true }
              : false
          }
          scroll={{ x: 1200 }}
          rowClassName={(row) =>
            !row.is_active || row.status === "closed" ? "farm-show-closed-row" : ""
          }
          rowSelection={
            canWrite
              ? {
                  selectedRowKeys: mergeSelectedIds,
                  onChange: (keys) => setMergeSelectedIds(keys as number[]),
                  getCheckboxProps: (row: RealPlot) => ({
                    disabled: merging || !row.is_active || row.status === "closed",
                  }),
                }
              : undefined
          }
          // Click en cualquier celda → abre el modal de detalle
          // completo de la parcela canónica. Filtramos clicks que
          // vienen de un `<a>` (los enlaces a auditorías) para que
          // mantengan su navegación propia.
          onRow={(row) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;
              if (target.closest("a")) return;
              if ((target as HTMLElement).closest(".ant-table-selection-column")) return;
              setSelectedPlotRealId(row.id);
            },
            style: { cursor: "pointer" },
          })}
        >
          <Table.Column
            title="Nombre"
            dataIndex="name"
            width={140}
            render={(v: string) => <strong>{v}</strong>}
            sorter={(a: RealPlot, b: RealPlot) => a.name.localeCompare(b.name)}
          />
          <Table.Column
            title="Variedad protegida"
            dataIndex="variety"
            width={180}
            render={(v: string | null) =>
              v ? <Tag color="blue">{v}</Tag> : "—"
            }
            sorter={(a: RealPlot, b: RealPlot) =>
              (a.variety || "").localeCompare(b.variety || "")
            }
          />
          <Table.Column
            title="Tipo"
            dataIndex="plot_type_name"
            width={110}
            render={(v: string | null) => v || "—"}
          />
          <Table.Column
            title="Sup. (ha)"
            dataIndex="surface"
            width={100}
            align="right"
            render={(v: number | null) => fmtNum(v)}
            sorter={(a: RealPlot, b: RealPlot) =>
              (a.surface ?? 0) - (b.surface ?? 0)
            }
          />
          <Table.Column
            title="Plantas"
            dataIndex="plants"
            width={90}
            align="right"
            render={(v: number | null) => (v != null ? v.toLocaleString("es-ES") : "—")}
          />
          <Table.Column
            title="Marco"
            dataIndex="plantation_frame"
            width={90}
            render={(v: string | null) => v || "—"}
          />
          <Table.Column
            title="Patrón"
            dataIndex="rootstock_name"
            width={110}
            render={(v: string | null) => v || "—"}
          />
          <Table.Column
            title="Plantación"
            dataIndex="crop_date"
            width={100}
            align="right"
            render={(v: number | null) => v || "—"}
          />
          <Table.Column
            title="Estado"
            width={130}
            render={(_: any, p: RealPlot) => (
              <Space direction="vertical" size={0}>
                {p.is_active ? (
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Abierta
                  </Tag>
                ) : (
                  <Tag color="default" icon={<StopOutlined />}>
                    Cerrada
                  </Tag>
                )}
                {p.closed_date && (
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {fmtDate(p.closed_date)}
                  </Typography.Text>
                )}
              </Space>
            )}
          />
          <Table.Column
            title="Auditoría origen"
            width={180}
            render={(_: any, p: RealPlot) =>
              p.created_in_audit ? (
                <a
                  onClick={() =>
                    navigate(`/audits/${p.created_in_audit!.id}`, {
                      state: { from: "farm", farmId: record.id },
                    })
                  }
                >
                  #{p.created_in_audit.id}
                  {p.created_in_audit.start_date &&
                    ` · ${fmtDate(p.created_in_audit.start_date)}`}
                </a>
              ) : (
                "—"
              )
            }
          />
          {canWrite && (
            <Table.Column
              title={t("common.actions", "Acciones")}
              width={130}
              fixed="right"
              align="center"
              render={(_: any, p: RealPlot) => (
                <Space size={4} onClick={(e) => e.stopPropagation()}>
                  <Tooltip
                    title={t(
                      "farms.show.edit_plot_real",
                      "Editar parcela canónica",
                    )}
                  >
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/farms/${record?.id}/plot-reals/${p.id}/edit`,
                        );
                      }}
                      aria-label={t(
                        "farms.show.edit_plot_real",
                        "Editar parcela canónica",
                      )}
                    />
                  </Tooltip>
                  <Tooltip
                    title={t(
                      "farms.show.move_plot_real",
                      "Trasladar a otra finca",
                    )}
                  >
                    <Button
                      size="small"
                      icon={<SwapOutlined />}
                      disabled={!p.is_active}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMoveTarget({ id: p.id, name: p.name ?? null });
                      }}
                      aria-label={t(
                        "farms.show.move_plot_real",
                        "Trasladar a otra finca",
                      )}
                    />
                  </Tooltip>
                  {p.geom_source === "historical" && (
                    <Popconfirm
                      title={t(
                        "farms.show.adopt_geom_title",
                        "Adoptar geometría histórica",
                      )}
                      description={
                        <div style={{ maxWidth: 320 }}>
                          {t(
                            "farms.show.adopt_geom_warning",
                            "La parcela canónica no tiene polígono propio, pero sí se dibujó en una auditoría anterior. Copiaremos ese polígono como geometría canónica. Podrás editarlo después desde el editor de la parcela.",
                          )}
                        </div>
                      }
                      okText={t("common.yes", "Sí, adoptar")}
                      cancelText={t("common.no", "No")}
                      onConfirm={(e) => {
                        e?.stopPropagation();
                        handleAdoptHistoricalGeom(p.id);
                      }}
                      onCancel={(e) => e?.stopPropagation()}
                    >
                      <Tooltip
                        title={t(
                          "farms.show.adopt_geom_action",
                          "Adoptar geometría histórica como canónica",
                        )}
                      >
                        <Button
                          size="small"
                          icon={<PushpinOutlined />}
                          loading={adoptingGeomId === p.id}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={t(
                            "farms.show.adopt_geom_action",
                            "Adoptar geometría histórica como canónica",
                          )}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                  <Popconfirm
                    title={t(
                      "farms.show.delete_plot_real_title",
                      "Eliminar parcela canónica",
                    )}
                    description={
                      <div style={{ maxWidth: 320 }}>
                        {t(
                          "farms.show.delete_plot_real_warning",
                          "Esta acción elimina la parcela canónica de la base de datos. Se borrarán también sus instantáneas por campaña y su histórico de revisiones. Las auditorías que la referencian conservan sus datos pero pierden el enlace canónico. No se puede deshacer.",
                        )}
                      </div>
                    }
                    okText={t("common.yes", "Sí, eliminar")}
                    cancelText={t("common.no", "No")}
                    okButtonProps={{ danger: true }}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDeletePlotReal(p.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                  >
                    <Tooltip
                      title={t(
                        "farms.show.delete_plot_real",
                        "Eliminar parcela canónica",
                      )}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingPlotRealId === p.id}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={t(
                          "farms.show.delete_plot_real",
                          "Eliminar parcela canónica",
                        )}
                      />
                    </Tooltip>
                  </Popconfirm>
                </Space>
              )}
            />
          )}
        </Table>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin parcelas canónicas materializadas todavía"
        />
      )}
      <style>{`
        .farm-show-closed-row > td {
          background: #fafafa !important;
          color: rgba(0, 0, 0, 0.45);
        }
      `}</style>
    </Card>
  );

  /* ============================================================== */
  /* Sección 5 — Auditorías                                          */
  /* ============================================================== */

  const auditsSection = (
    <Card
      size="small"
      title={
        <Space>
          <strong>{t("farms.show.audits_title", "Auditorías")}</strong>
          <Tag>{record.audits.length}</Tag>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 16 }}
    >
      {record.audits.length > 0 ? (
        <Table
          dataSource={record.audits}
          rowKey="id"
          size="small"
          pagination={
            record.audits.length > 10
              ? { pageSize: 10, showSizeChanger: true }
              : false
          }
          scroll={{ x: 1200 }}
          onRow={(audit) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;
              if (target.closest("button") || target.closest("a")) return;
              navigate(`/audits/${audit.id}`, {
                state: {
                  from: "farm",
                  farmId: record.id,
                  // Forward the farm's own listSnapshot so when the
                  // user navigates back via audit→farm→/farms, all
                  // filters and scroll position are restored.
                  farmListSnapshot: listSnapshot,
                  farmFromState: fromState,
                },
              });
            },
            style: { cursor: "pointer" },
          })}
        >
          <Table.Column
            title="Fecha"
            dataIndex="start_date"
            width={110}
            render={fmtDate}
            sorter={(a: AuditItem, b: AuditItem) =>
              (a.start_date || "").localeCompare(b.start_date || "")
            }
          />
          <Table.Column
            title="Año campaña"
            dataIndex="season_year"
            width={110}
            align="right"
            render={(v: number | null) => v || "—"}
          />
          <Table.Column
            title="Tipo"
            dataIndex="audit_type_name"
            width={110}
            render={(v: string | null) =>
              v ? <Tag color="cyan">{v}</Tag> : "—"
            }
          />
          <Table.Column
            title="Estado"
            dataIndex="audit_state_name"
            width={140}
            render={(v: string | null) =>
              v ? <Tag color={auditStateColor(v)}>{v}</Tag> : "—"
            }
            sorter={(a: AuditItem, b: AuditItem) =>
              (a.audit_state_name || "").localeCompare(
                b.audit_state_name || "",
              )
            }
          />
          <Table.Column
            title="Auditor"
            dataIndex="auditor_name"
            width={160}
            render={(v: string | null) => v || "—"}
          />
          <Table.Column
            title="Variedades"
            dataIndex="varieties"
            width={200}
            render={(varieties: VarietyChip[] | undefined) =>
              varieties && varieties.length > 0 ? (
                <Space size={4} wrap>
                  {varieties.map((v) => (
                    <Tag key={v.code} color="blue" style={{ margin: 0 }}>
                      {v.name}
                    </Tag>
                  ))}
                </Space>
              ) : (
                "—"
              )
            }
          />
          <Table.Column
            title="# Parcelas"
            dataIndex="audit_plots_count"
            width={100}
            align="right"
            render={(v: number | undefined) => v ?? "—"}
          />
          <Table.Column
            title="Aforo (kg)"
            dataIndex="total_kg"
            width={120}
            align="right"
            render={fmtKg}
          />
          <Table.Column
            title="Est. Productor (kg)"
            dataIndex="stimated_production"
            width={150}
            align="right"
            render={fmtKg}
          />
          <Table.Column
            title="Referencia"
            dataIndex="reference"
            width={160}
            render={(v: string | null) => v || "—"}
          />
          <Table.Column
            title=""
            width={50}
            align="right"
            render={(_: any, audit: AuditItem) =>
              audit.audit_act_file ? (
                <Button
                  type="text"
                  size="small"
                  icon={<FileTextOutlined />}
                  onClick={() => window.open(audit.audit_act_file!, "_blank")}
                  title="Ver acta"
                />
              ) : null
            }
          />
        </Table>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(
            "farms.show.no_audits",
            "No hay auditorías registradas",
          )}
        />
      )}
    </Card>
  );

  /* ============================================================== */
  /* Sección 6 — Producción por campaña y variedad                   */
  /* ============================================================== */

  const productionSection = (
    <Card
      size="small"
      title={
        <Space>
          <strong>
            {t(
              "farms.show.production_section",
              "Producción por campaña y variedad",
            )}
          </strong>
          <Tag>{record.production_records.length}</Tag>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 16 }}
    >
      {record.production_records.length > 0 ? (
        <Table
          dataSource={record.production_records}
          rowKey={(r) => `${r.season_year}-${r.variety_id ?? "n"}`}
          size="small"
          pagination={
            record.production_records.length > 15
              ? { pageSize: 15, showSizeChanger: true }
              : false
          }
          scroll={{ x: 900 }}
        >
          <Table.Column
            title="Año"
            dataIndex="season_year"
            width={90}
            sorter={(a: ProductionRecord, b: ProductionRecord) =>
              (a.season_year ?? 0) - (b.season_year ?? 0)
            }
            defaultSortOrder="descend"
            render={(v: number | null) => v || "—"}
          />
          <Table.Column
            title="Variedad"
            dataIndex="variety_name"
            width={180}
            render={(v: string | null) =>
              v ? <Tag color="blue">{v}</Tag> : "—"
            }
            sorter={(a: ProductionRecord, b: ProductionRecord) =>
              (a.variety_name || "").localeCompare(b.variety_name || "")
            }
          />
          <Table.Column
            title="Estimación auditor (kg)"
            dataIndex="audited_kg"
            width={170}
            align="right"
            render={fmtKg}
            sorter={(a: ProductionRecord, b: ProductionRecord) =>
              (a.audited_kg ?? 0) - (b.audited_kg ?? 0)
            }
          />
          <Table.Column
            title="Producción real (kg)"
            dataIndex="produced_kg"
            width={170}
            align="right"
            render={fmtKg}
            sorter={(a: ProductionRecord, b: ProductionRecord) =>
              (a.produced_kg ?? 0) - (b.produced_kg ?? 0)
            }
          />
          <Table.Column
            title="Diferencia"
            width={170}
            align="right"
            render={(_: any, r: ProductionRecord) => {
              const audited = r.audited_kg ?? 0;
              const produced = r.produced_kg ?? 0;
              if (audited === 0 && produced === 0) return "—";
              const diff = produced - audited;
              const pct = audited > 0 ? (Math.abs(diff) / audited) * 100 : 0;
              const color =
                diff === 0 ? "#1677ff" : diff > 0 ? "#52c41a" : "#f5222d";
              const sign = diff === 0 ? "" : diff > 0 ? "+" : "";
              return (
                <span style={{ color, fontWeight: 500 }}>
                  {sign}
                  {fmtKg(diff)}
                  {audited > 0 && ` (${pct.toFixed(1)}%)`}
                </span>
              );
            }}
          />
          <Table.Column
            title="Packers"
            dataIndex="packers"
            ellipsis
            render={(v: string) => v || "—"}
          />
        </Table>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin registros de producción todavía"
        />
      )}
    </Card>
  );

  /* ============================================================== */

  const mapSection = (
    <FarmMapCard
      farmName={record.name}
      producerName={record.producer_name}
      locationLine={
        record.town_name && record.province_name
          ? `${record.town_name} (${record.province_name})`
          : record.town_name || record.province_name || null
      }
      declaredPlots={record.reference_plots.map((p) => ({
        id: p.id,
        name: p.sigpac_ref,
        surface: p.surface,
        surface_gps: p.surface_gps,
        geom_geojson: p.geom_geojson,
        variety_name: p.variety_name,
      }))}
      canonicalPlots={record.real_plots
        // Las parcelas dadas de baja (is_active=false o status='closed')
        // se mantienen en el listado tabular para conservar histórico
        // pero NO se pintan sobre el mapa — refleja el estado físico
        // real de la finca después del cierre.
        .filter((p) => p.is_active !== false && p.status !== "closed")
        .map((p) => ({
          id: p.id,
          name: p.name,
          surface: p.surface,
          surface_gps: p.surface_gps,
          geom_geojson: p.geom_geojson,
          variety_name: p.variety,
          plants: p.plants,
          crop_date: p.crop_date,
          plantation_date: p.plantation_date,
          plot_type_name: p.plot_type_name,
          rootstock_name: p.rootstock_name,
          plantation_frame: p.plantation_frame,
          season_name: p.season_name,
          is_active: p.is_active,
          status: p.status,
          closed_date: p.closed_date,
        }))}
    />
  );

  return (
    <div>
      {topBar}
      <Row gutter={16}>
        <Col xs={24} lg={14}>
          {headerInfoSection}
          {varietiesSection}
          {referencePlotsSection}
        </Col>
        <Col xs={24} lg={10}>
          {mapSection}
        </Col>
      </Row>
      {/* PR5 — Validación de superficie per-variedad (estado actual).
          El componente se auto-oculta si !isAdmin. */}
      {record?.id && (
        <SurfaceValidationCard scope="farm" id={record.id} isAdmin={isAdmin} />
      )}
      {realPlotsSection}
      {auditsSection}
      {productionSection}

      <RealPlotDetailModal
        plotRealId={selectedPlotRealId}
        open={selectedPlotRealId != null}
        onClose={() => setSelectedPlotRealId(null)}
        onNavigateToAudit={(auditId) => {
          setSelectedPlotRealId(null);
          navigate(`/audits/${auditId}`, {
            state: { from: "farm", farmId: record.id },
          });
        }}
      />

      {moveTarget && (
        <MovePlotRealModal
          open={moveTarget != null}
          onClose={() => setMoveTarget(null)}
          plot={moveTarget}
          sourceFarm={{
            id: record.id,
            name: record.name,
            producer_id: record.producer_id,
            producer_name: record.producer_name,
          }}
          onMoved={(res) => {
            // El toast + notification los dispara el propio modal.
            // Aquí solo refrescamos el detalle y, si la finca origen
            // se desactivó, abrimos el modal de aviso adicional.
            if (res.source_farm_deactivated) {
              modal.warning({
                title: t(
                  "farms.move_plot.source_deactivated_title",
                  "Finca origen desactivada",
                ),
                content: t(
                  "farms.move_plot.source_deactivated_body",
                  "La finca {{name}} se ha desactivado automáticamente porque se ha quedado sin parcelas canónicas activas.",
                  { name: record.name },
                ),
              });
            }
            query.refetch();
          }}
        />
      )}
    </div>
  );
};
